import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Typography,
    Box,
    MenuItem,
    FormControl,
    Select,
    TablePagination,
    Dialog,
    DialogContent,
    DialogActions,
    Autocomplete,
    TextField,
    Button,
    Chip,
    Tooltip,
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Close as CloseIcon,
    Link as LinkIcon,
    Save as SaveIcon,
    BugReport as BugIcon,
    DragIndicator as DragIcon,
} from '@mui/icons-material';
import { issueService } from '../services/issueService';
import { releasesAPI } from '../services/api';
import { getReleaseScopes } from '../services/releaseManagementApi';
import ResizableTableCell from './ResizableTableCell';
import TruncatedText from './TruncatedText';
import SlackMessageDialog from './SlackMessageDialog';
import CreateJiraBugDialog from './CreateJiraBugDialog';
import IssueTrackerSummary from './IssueTrackerSummary';
import IssueDetailPanel from './IssueDetailPanel';
import api from '../services/api';

/* ================================================================ */
/*  Column configuration                                            */
/* ================================================================ */
const STORAGE_KEY = 'issueTable_columnOrder';

const COLUMN_DEFS = {
    id:        { label: 'ID',          minWidth: 60,  initialWidth: 80,  align: 'center', filterKey: 'id' },
    title:     { label: 'Title',       minWidth: 200, initialWidth: 350, align: 'left',   filterKey: 'title' },
    storyId:   { label: 'Story ID',    minWidth: 100, initialWidth: 120, align: 'center', filterKey: 'storyId' },
    module:    { label: 'Module',      minWidth: 120, initialWidth: 150, align: 'center', filterKey: 'module' },
    subModule: { label: 'Sub Module',  minWidth: 120, initialWidth: 150, align: 'center', filterKey: 'subModule' },
    feature:   { label: 'Feature',     minWidth: 120, initialWidth: 150, align: 'center', filterKey: 'feature' },
    scope:     { label: 'Scope',        minWidth: 100, initialWidth: 130, align: 'center', filterKey: 'scope' },
    status:    { label: 'Status',      minWidth: 120, initialWidth: 150, align: 'center', filterKey: 'status',   filterType: 'select' },
    priority:  { label: 'Priority',    minWidth: 100, initialWidth: 120, align: 'center', filterKey: 'priority', filterType: 'select' },
    assignee:  { label: 'Assigned To', minWidth: 150, initialWidth: 180, align: 'center', filterKey: 'assignee' },
    reporter:  { label: 'Reporter',    minWidth: 120, initialWidth: 150, align: 'center', filterKey: 'reporter' },
    bugId:     { label: 'Bug ID',      minWidth: 100, initialWidth: 120, align: 'center', filterKey: 'bugId' },
    actions:   { label: 'Actions',     minWidth: 120, initialWidth: 150, align: 'center', filterKey: null },
};

// Default column order — Bug ID comes after Reporter
const DEFAULT_COLUMN_ORDER = ['id', 'title', 'storyId', 'module', 'subModule', 'feature', 'scope', 'status', 'priority', 'assignee', 'reporter', 'bugId', 'actions'];

function loadColumnOrder() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) {
            const parsed = JSON.parse(stored);
            // Validate: must contain exactly the same column ids
            if (Array.isArray(parsed) && parsed.length === DEFAULT_COLUMN_ORDER.length && DEFAULT_COLUMN_ORDER.every(c => parsed.includes(c))) {
                return parsed;
            }
        }
    } catch { /* ignore */ }
    return DEFAULT_COLUMN_ORDER;
}

const IssueRow = ({ issue, onEdit, onDelete, onUpdate, jiraUsers, jiraUsersMap, modules, subModules, features, scopes, jiraConnected = false, onRefresh, onRowClick, isSelected = false, columnOrder }) => {
    const [status, setStatus] = useState(issue.status);
    const [priority, setPriority] = useState(issue.priority);
    const [moduleId, setModuleId] = useState(issue.module_id || '');
    const [subModuleId, setSubModuleId] = useState(issue.sub_module_id || '');
    const [featureId, setFeatureId] = useState(issue.feature_id || '');
    const [scopeId, setScopeId] = useState(issue.scope_id || '');
    const [jiraAssigneeId, setJiraAssigneeId] = useState(issue.jira_assignee_id || '');
    const [linkDialogOpen, setLinkDialogOpen] = useState(false);
    const [stories, setStories] = useState([]);
    const [selectedStory, setSelectedStory] = useState(null);
    const [slackDialogOpen, setSlackDialogOpen] = useState(false);
    const [createBugDialogOpen, setCreateBugDialogOpen] = useState(false);
    const [localJiraBugId, setLocalJiraBugId] = useState(issue.jira_bug_id);
    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [editedTitle, setEditedTitle] = useState(issue.title);

    // Update local state when issue prop changes (e.g. side panel edits)
    useEffect(() => {
        setLocalJiraBugId(issue.jira_bug_id);
        setEditedTitle(issue.title);
        setStatus(issue.status);
        setPriority(issue.priority);
        setModuleId(issue.module_id || '');
        setSubModuleId(issue.sub_module_id || '');
        setFeatureId(issue.feature_id || '');
        setScopeId(issue.scope_id || '');
        setJiraAssigneeId(issue.jira_assignee_id || '');
    }, [issue.jira_bug_id, issue.title, issue.status, issue.priority, issue.module_id, issue.sub_module_id, issue.feature_id, issue.scope_id, issue.jira_assignee_id]);

    const handleStatusChange = async (newStatus) => {
        try {
            setStatus(newStatus);
            await onUpdate(issue.id, { status: newStatus });
        } catch (error) {
            setStatus(issue.status); // Revert on error
            console.error('Failed to update status:', error);
        }
    };

    const handlePriorityChange = async (newPriority) => {
        try {
            setPriority(newPriority);
            await onUpdate(issue.id, { priority: newPriority });
        } catch (error) {
            setPriority(issue.priority); // Revert on error
            console.error('Failed to update priority:', error);
        }
    };

    const handleModuleChange = async (newModuleId) => {
        try {
            setModuleId(newModuleId);
            // Cascade: clear sub_module and feature when module changes
            setSubModuleId('');
            setFeatureId('');
            await onUpdate(issue.id, { module_id: newModuleId || null, sub_module_id: null, feature_id: null });
        } catch (error) {
            setModuleId(issue.module_id); // Revert on error
            setSubModuleId(issue.sub_module_id || '');
            setFeatureId(issue.feature_id || '');
            console.error('Failed to update module:', error);
        }
    };

    const handleSubModuleChange = async (newSubModuleId) => {
        try {
            setSubModuleId(newSubModuleId);
            // Cascade: clear feature when sub_module changes
            setFeatureId('');
            await onUpdate(issue.id, { sub_module_id: newSubModuleId || null, feature_id: null });
        } catch (error) {
            setSubModuleId(issue.sub_module_id || ''); // Revert on error
            setFeatureId(issue.feature_id || '');
            console.error('Failed to update sub_module:', error);
        }
    };

    const handleFeatureChange = async (newFeatureId) => {
        try {
            setFeatureId(newFeatureId);
            await onUpdate(issue.id, { feature_id: newFeatureId || null });
        } catch (error) {
            setFeatureId(issue.feature_id || ''); // Revert on error
            console.error('Failed to update feature:', error);
        }
    };

    const handleScopeChange = async (newScopeId) => {
        try {
            setScopeId(newScopeId);
            await onUpdate(issue.id, { scope_id: newScopeId || null });
        } catch (error) {
            setScopeId(issue.scope_id || ''); // Revert on error
            console.error('Failed to update scope:', error);
        }
    };

    const handleTitleSave = async () => {
        const trimmed = editedTitle.trim();
        if (!trimmed || trimmed === issue.title) {
            setEditedTitle(issue.title);
            setIsEditingTitle(false);
            return;
        }
        try {
            await onUpdate(issue.id, { title: trimmed });
            setIsEditingTitle(false);
        } catch (error) {
            setEditedTitle(issue.title); // Revert on error
            setIsEditingTitle(false);
            console.error('Failed to update title:', error);
        }
    };

    const handleTitleCancel = () => {
        setEditedTitle(issue.title);
        setIsEditingTitle(false);
    };

    const handleAssigneeChange = async (newAssigneeId) => {
        try {
            setJiraAssigneeId(newAssigneeId);
            // Find the selected user to get their display name and email
            const selectedUser = jiraUsers.find(user => user.accountId === newAssigneeId);
            const assigneeName = selectedUser ? selectedUser.displayName : null;
            const assigneeEmail = selectedUser ? selectedUser.emailAddress : null;
            
            await onUpdate(issue.id, { 
                jira_assignee_id: newAssigneeId || null,
                jira_assignee_name: assigneeName,
                jira_assignee_email: assigneeEmail
            });
        } catch (error) {
            setJiraAssigneeId(issue.jira_assignee_id); // Revert on error
            console.error('Failed to update assignee:', error);
        }
    };

    const handleOpenLinkDialog = async () => {
        try {
            // Fetch all stories from JIRA
            const response = await api.get('/jira-stories/');
            setStories(response.data);
            setLinkDialogOpen(true);
        } catch (error) {
            console.error('Failed to fetch stories:', error);
            alert('Failed to load stories');
        }
    };

    const handleLinkStory = async () => {
        if (!selectedStory) return;
        
        try {
            await onUpdate(issue.id, { jira_story_id: selectedStory });
            setLinkDialogOpen(false);
            alert(`Issue linked to story ${selectedStory}`);
        } catch (error) {
            console.error('Failed to link story:', error);
            alert('Failed to link story');
        }
    };

    const handleCreateBugSuccess = (response) => {
        // Update local state immediately
        setLocalJiraBugId(response.jira_bug_id);
        // Refresh the parent list
        if (onRefresh) {
            onRefresh();
        }
    };

    return (
        <React.Fragment>
            <TableRow
                sx={{
                    '& > *': { borderBottom: 'unset' },
                    ...(isSelected ? { bgcolor: 'action.selected' } : { '&:hover': { bgcolor: 'action.hover' } }),
                }}
            >
                {(columnOrder || DEFAULT_COLUMN_ORDER).map(colId => {
                    const def = COLUMN_DEFS[colId];
                    switch (colId) {
                        case 'id':
                            return (
                                <TableCell key={colId} align="center" sx={{ width: def.initialWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {issue.id}
                                </TableCell>
                            );
                        case 'title':
                            return (
                                <TableCell key={colId} align="left" sx={{ width: def.initialWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'pointer' }} onClick={() => !isEditingTitle && onRowClick && onRowClick(issue)}>
                                    {isEditingTitle ? (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <TextField value={editedTitle} onChange={(e) => setEditedTitle(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') handleTitleSave(); if (e.key === 'Escape') handleTitleCancel(); }} size="small" fullWidth autoFocus variant="standard" sx={{ '& .MuiInputBase-input': { fontSize: '0.875rem', py: 0.5 } }} />
                                            <IconButton size="small" onClick={handleTitleSave} color="primary" sx={{ p: 0.25 }}><SaveIcon sx={{ fontSize: 16 }} /></IconButton>
                                            <IconButton size="small" onClick={handleTitleCancel} sx={{ p: 0.25 }}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                                        </Box>
                                    ) : (
                                        <Box sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 }, px: 0.5, py: 0.25 }} onDoubleClick={(e) => { e.stopPropagation(); setIsEditingTitle(true); }} title="Double-click to edit title">
                                            <TruncatedText text={editedTitle} />
                                        </Box>
                                    )}
                                </TableCell>
                            );
                        case 'storyId':
                            return (
                                <TableCell key={colId} align="center" sx={{ width: def.initialWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {issue.jira_story_id ? (
                                        <Chip label={issue.jira_story_id} size="small" color="primary" variant="outlined" component="a" href={`https://centime.atlassian.net/browse/${issue.jira_story_id}`} target="_blank" rel="noopener noreferrer" clickable sx={{ cursor: 'pointer' }} />
                                    ) : '-'}
                                </TableCell>
                            );
                        case 'bugId':
                            return (
                                <TableCell key={colId} align="center" sx={{ width: def.initialWidth, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {localJiraBugId ? (
                                        <Chip label={localJiraBugId} size="small" color="error" variant="outlined" component="a" href={`https://centime.atlassian.net/browse/${localJiraBugId}`} target="_blank" rel="noopener noreferrer" clickable sx={{ cursor: 'pointer' }} icon={<BugIcon sx={{ fontSize: 16 }} />} />
                                    ) : '-'}
                                </TableCell>
                            );
                        case 'module':
                            return (
                                <TableCell key={colId} align="center" sx={{ width: def.initialWidth }}>
                                    <FormControl fullWidth size="small">
                                        <Select value={moduleId} onChange={(e) => handleModuleChange(e.target.value)} variant="standard" disableUnderline displayEmpty sx={{ fontSize: '0.875rem', '& .MuiSelect-select': { py: 0.5, px: 1, textAlign: 'center' }, '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 } }}>
                                            <MenuItem value="">-</MenuItem>
                                            {modules.map((m) => (<MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                            );
                        case 'subModule':
                            return (
                                <TableCell key={colId} align="center" sx={{ width: def.initialWidth }}>
                                    <FormControl fullWidth size="small">
                                        <Select value={subModuleId} onChange={(e) => handleSubModuleChange(e.target.value)} variant="standard" disableUnderline displayEmpty disabled={!moduleId} sx={{ fontSize: '0.875rem', '& .MuiSelect-select': { py: 0.5, px: 1, textAlign: 'center' }, '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 } }}>
                                            <MenuItem value="">-</MenuItem>
                                            {subModules.filter(sm => sm.module_id === moduleId).map((sm) => (<MenuItem key={sm.id} value={sm.id}>{sm.name}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                            );
                        case 'feature':
                            return (
                                <TableCell key={colId} align="center" sx={{ width: def.initialWidth }}>
                                    <FormControl fullWidth size="small">
                                        <Select value={featureId} onChange={(e) => handleFeatureChange(e.target.value)} variant="standard" disableUnderline displayEmpty disabled={!subModuleId} sx={{ fontSize: '0.875rem', '& .MuiSelect-select': { py: 0.5, px: 1, textAlign: 'center' }, '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 } }}>
                                            <MenuItem value="">-</MenuItem>
                                            {features.filter(f => f.sub_module_id === subModuleId).map((f) => (<MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                            );
                        case 'scope':
                            return (
                                <TableCell key={colId} align="center" sx={{ width: def.initialWidth }}>
                                    <FormControl fullWidth size="small">
                                        <Select value={scopeId} onChange={(e) => handleScopeChange(e.target.value)} variant="standard" disableUnderline displayEmpty disabled={!issue.release_id} sx={{ fontSize: '0.875rem', '& .MuiSelect-select': { py: 0.5, px: 1, textAlign: 'center' }, '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 } }}>
                                            <MenuItem value="">-</MenuItem>
                                            {scopes.filter(s => s.release_id === issue.release_id).map((s) => (<MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>))}
                                        </Select>
                                    </FormControl>
                                </TableCell>
                            );
                        case 'status':
                            return (
                                <TableCell key={colId} align="center" sx={{ width: 'auto', minWidth: 100 }}>
                                    <FormControl size="small">
                                        <Select value={status} onChange={(e) => handleStatusChange(e.target.value)} variant="standard" disableUnderline
                                            renderValue={(val) => (
                                                <Chip label={val} size="small" sx={{ fontWeight: 700, fontSize: '0.75rem', height: 26, color: '#fff', ...({ 'Open': { bgcolor: '#1976d2' }, 'In Development': { bgcolor: '#ed6c02' }, 'Resolved': { bgcolor: '#2e7d32' }, 'Fix Deployed': { bgcolor: '#0288d1' }, 'Testing in progress': { bgcolor: '#7b1fa2' }, 'Fix not working': { bgcolor: '#d32f2f' }, 'Closed': { bgcolor: '#546e7a' }, 'Backlog': { bgcolor: '#78909c' }, 'Defaulter': { bgcolor: '#880e4f' } }[val] || { bgcolor: '#757575' }) }} />
                                            )}
                                            sx={{ '& .MuiSelect-select': { py: 0.25, px: 0, display: 'flex', justifyContent: 'center' } }}
                                        >
                                            <MenuItem value="Open">Open</MenuItem>
                                            <MenuItem value="In Development">In Development</MenuItem>
                                            <MenuItem value="Resolved">Resolved</MenuItem>
                                            <MenuItem value="Fix Deployed">Fix Deployed</MenuItem>
                                            <MenuItem value="Testing in progress">Testing in progress</MenuItem>
                                            <MenuItem value="Fix not working">Fix not working</MenuItem>
                                            <MenuItem value="Closed">Closed</MenuItem>
                                            <MenuItem value="Backlog">Backlog</MenuItem>
                                            <MenuItem value="Defaulter">Defaulter</MenuItem>
                                        </Select>
                                    </FormControl>
                                </TableCell>
                            );
                        case 'priority':
                            return (
                                <TableCell key={colId} align="center" sx={{ width: 'auto', minWidth: 80 }}>
                                    <FormControl size="small">
                                        <Select value={priority} onChange={(e) => handlePriorityChange(e.target.value)} variant="standard" disableUnderline
                                            renderValue={(val) => (
                                                <Chip label={val} size="small" sx={{ fontWeight: 700, fontSize: '0.75rem', height: 26, color: '#fff', ...({ 'Critical': { bgcolor: '#d32f2f' }, 'High': { bgcolor: '#e65100' }, 'Medium': { bgcolor: '#f57c00' }, 'Low': { bgcolor: '#388e3c' } }[val] || { bgcolor: '#757575' }) }} />
                                            )}
                                            sx={{ '& .MuiSelect-select': { py: 0.25, px: 0, display: 'flex', justifyContent: 'center' } }}
                                        >
                                            <MenuItem value="Critical">Critical</MenuItem>
                                            <MenuItem value="High">High</MenuItem>
                                            <MenuItem value="Medium">Medium</MenuItem>
                                            <MenuItem value="Low">Low</MenuItem>
                                        </Select>
                                    </FormControl>
                                </TableCell>
                            );
                        case 'assignee':
                            return (
                                <TableCell key={colId} align="center" sx={{ width: def.initialWidth }}>
                                    <Autocomplete
                                        value={jiraAssigneeId ? (jiraUsersMap[jiraAssigneeId] || null) : null}
                                        onChange={(event, newValue) => handleAssigneeChange(newValue ? newValue.accountId : '')}
                                        options={jiraUsers}
                                        getOptionLabel={(option) => option.displayName || ''}
                                        isOptionEqualToValue={(option, value) => option.accountId === value.accountId}
                                        size="small"
                                        renderInput={(params) => (
                                            <TextField {...params} variant="standard" placeholder="Unassigned" InputProps={{ ...params.InputProps, disableUnderline: true, sx: { fontSize: '0.875rem', py: 0, px: 0.5 } }} />
                                        )}
                                        sx={{ '& .MuiAutocomplete-input': { textAlign: 'center', fontSize: '0.875rem', py: 0.5, px: 1 }, '&:hover': { backgroundColor: 'action.hover', borderRadius: 1 } }}
                                    />
                                </TableCell>
                            );
                        case 'reporter':
                            return (
                                <TableCell key={colId} align="center" sx={{ width: def.initialWidth }}>
                                    <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{issue.reporter_name || '-'}</Typography>
                                </TableCell>
                            );
                        case 'actions':
                            return (
                                <TableCell key={colId} align="center" sx={{ width: 100 }}>
                                    <Tooltip title="Send Slack Message">
                                        <IconButton size="small" onClick={(e) => { e.stopPropagation(); setSlackDialogOpen(true); }} sx={{ p: 0.5, '&:hover': { bgcolor: 'rgba(54, 197, 240, 0.1)' } }}>
                                            <img src="/icons/slack-plane.svg" alt="Send Slack DM" style={{ width: 22, height: 22 }} />
                                        </IconButton>
                                    </Tooltip>
                                    {!localJiraBugId && (
                                        <Tooltip title={jiraConnected ? "Create Bug in JIRA" : "Connect JIRA account in Settings to create bugs"}>
                                            <span>
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); if (jiraConnected) setCreateBugDialogOpen(true); }} disabled={!jiraConnected} color="error" sx={{ p: 0.5, opacity: jiraConnected ? 1 : 0.5 }}>
                                                    <BugIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    <IconButton size="small" onClick={() => onDelete(issue.id)} title="Delete Issue" color="error"><DeleteIcon fontSize="small" /></IconButton>
                                    <IconButton size="small" title="Link to Story" color="primary" onClick={(e) => { e.stopPropagation(); handleOpenLinkDialog(); }}><LinkIcon fontSize="small" /></IconButton>
                                </TableCell>
                            );
                        default:
                            return null;
                    }
                })}
            </TableRow>
            
            {/* Slack Message Dialog */}
            <SlackMessageDialog
                open={slackDialogOpen}
                onClose={() => setSlackDialogOpen(false)}
                ticket={{
                    key: issue.jira_story_id || `ISSUE-${issue.id}`,
                    summary: issue.title,
                    priority: issue.priority,
                    status: issue.status,
                    assignee: issue.jira_assignee_name || 'Unassigned'
                }}
                defaultRecipientEmail={issue.jira_assignee_email}
                contextType="issue"
                issueId={issue.id}
                releaseId={issue.release_id}
            />
            
            {/* Create JIRA Bug Dialog */}
            <CreateJiraBugDialog
                open={createBugDialogOpen}
                onClose={() => setCreateBugDialogOpen(false)}
                issue={issue}
                onSuccess={handleCreateBugSuccess}
            />
            
            {/* Link to Story Dialog */}
            <Dialog
                open={linkDialogOpen}
                onClose={() => setLinkDialogOpen(false)}
                maxWidth="md"
                fullWidth
            >
                <DialogActions sx={{ p: 1 }}>
                    <Typography variant="h6" sx={{ flexGrow: 1, px: 2 }}>
                        Link Issue to Story
                    </Typography>
                    <IconButton onClick={() => setLinkDialogOpen(false)}>
                        <CloseIcon />
                    </IconButton>
                </DialogActions>
                <DialogContent>
                    <Autocomplete
                        value={selectedStory}
                        onChange={(event, newValue) => setSelectedStory(newValue)}
                        options={stories.map(story => story.story_id)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Select Story"
                                placeholder="Search for a story..."
                                fullWidth
                            />
                        )}
                        renderOption={(props, option) => {
                            const story = stories.find(s => s.story_id === option);
                            return (
                                <Box component="li" {...props}>
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                            {option}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {story?.title}
                                        </Typography>
                                    </Box>
                                </Box>
                            );
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
                    <Button 
                        onClick={handleLinkStory} 
                        variant="contained" 
                        disabled={!selectedStory}
                    >
                        Link Story
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
};

const IssueList = ({ 
    onEdit, 
    onDelete: onDeleteCallback,  // Callback after successful deletion
    refreshTrigger, 
    issues: externalIssues,  // Issues passed from parent (optional)
    loading: externalLoading,  // Loading state from parent (optional)
    showModuleColumn = true,
    highlightIds = [],  // IDs to highlight (from smart search)
    defaultReleaseId = null,  // Filter by release ID (from URL param)
    autoExpandIssueId = null,  // Issue ID to auto-expand (from URL param)
    defaultStatus = null,      // Pre-fill status filter (from assistant navigation)
    defaultPriority = null,    // Pre-fill priority filter (from assistant navigation)
    defaultScope = null,       // Pre-fill scope filter (from swimlane navigation)
}) => {
    const [internalIssues, setInternalIssues] = useState([]);
    const [internalLoading, setInternalLoading] = useState(true);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [jiraUsers, setJiraUsers] = useState([]);
    const [modules, setModules] = useState([]);
    const [subModules, setSubModules] = useState([]);
    const [features, setFeatures] = useState([]);
    const [jiraConnected, setJiraConnected] = useState(false);
    const [releases, setReleases] = useState([]);
    const [scopes, setScopes] = useState([]);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [panelOpen, setPanelOpen] = useState(false);
    const [columnOrder, setColumnOrder] = useState(loadColumnOrder);
    const dragColRef = useRef(null);
    const [filters, setFilters] = useState({
        id: '',
        title: '',
        storyId: '',
        bugId: '',
        module: '',
        subModule: '',
        feature: '',
        scope: defaultScope || '',
        status: defaultStatus || '',
        priority: defaultPriority || '',
        assignee: '',
        reporter: ''
    });

    // Use external issues if provided, otherwise use internal state
    const issues = externalIssues !== undefined ? externalIssues : internalIssues;
    const loading = externalLoading !== undefined ? externalLoading : internalLoading;
    const setIssues = externalIssues !== undefined ? () => {} : setInternalIssues;

    // React to external filter changes (e.g., from IssueStatsBoard tile click or URL changes)
    useEffect(() => {
        setFilters(prev => {
            const newStatus = defaultStatus || '';
            if (prev.status !== newStatus) {
                return { ...prev, status: newStatus };
            }
            return prev;
        });
        setPage(0);
    }, [defaultStatus]);

    useEffect(() => {
        setFilters(prev => {
            const newScope = defaultScope || '';
            if (prev.scope !== newScope) {
                return { ...prev, scope: newScope };
            }
            return prev;
        });
        setPage(0);
    }, [defaultScope]);

    // Handle tile click in IssueTrackerSummary: toggle status filter
    const handleTileClick = useCallback((statusValue) => {
        setFilters(prev => ({
            ...prev,
            status: statusValue === null ? '' : (prev.status === statusValue ? '' : statusValue)
        }));
        setPage(0);
    }, []);

    // Handle module chip click: toggle module filter
    const handleModuleChipClick = useCallback((moduleName) => {
        setFilters(prev => ({
            ...prev,
            module: prev.module === moduleName ? '' : moduleName
        }));
        setPage(0);
    }, []);

    // Handle row click – open side panel
    const handleRowClick = useCallback((issue) => {
        setSelectedIssue(issue);
        setPanelOpen(true);
    }, []);

    const handlePanelClose = useCallback(() => {
        setPanelOpen(false);
    }, []);

    // When the panel signals an update, apply changes locally without resetting filters
    const handlePanelIssueUpdated = useCallback(async (updates) => {
        if (!selectedIssue?.id) return;
        // Immediately apply the updates to the issues list for instant table feedback
        if (updates) {
            setInternalIssues(prev => prev.map(issue =>
                issue.id === selectedIssue.id ? { ...issue, ...updates } : issue
            ));
        }
        // Refresh the selected issue with full server data
        try {
            const freshIssue = await issueService.getById(selectedIssue.id);
            setSelectedIssue(freshIssue);
            // Sync the full server data back into the list
            setInternalIssues(prev => prev.map(issue =>
                issue.id === selectedIssue.id ? freshIssue : issue
            ));
        } catch (err) {
            console.error('Error refreshing selected issue:', err);
        }
    }, [selectedIssue?.id]);

    /* ---------- Column drag-and-drop ---------- */
    const handleDragStart = useCallback((colId) => {
        dragColRef.current = colId;
    }, []);

    const handleDragOver = useCallback((e) => {
        e.preventDefault();
    }, []);

    const handleDrop = useCallback((targetColId) => {
        const srcCol = dragColRef.current;
        if (!srcCol || srcCol === targetColId) return;
        setColumnOrder(prev => {
            const next = [...prev];
            const srcIdx = next.indexOf(srcCol);
            const tgtIdx = next.indexOf(targetColId);
            if (srcIdx === -1 || tgtIdx === -1) return prev;
            next.splice(srcIdx, 1);
            next.splice(tgtIdx, 0, srcCol);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
            return next;
        });
        dragColRef.current = null;
    }, []);

    const handleResetColumnOrder = useCallback(() => {
        setColumnOrder(DEFAULT_COLUMN_ORDER);
        localStorage.removeItem(STORAGE_KEY);
    }, []);

    /* ---------- Filter cell renderer ---------- */
    const renderFilterCell = useCallback((colId) => {
        const def = COLUMN_DEFS[colId];
        if (!def.filterKey) return <TableCell key={colId} />;
        if (colId === 'status') {
            return (
                <TableCell key={colId}>
                    <Select size="small" displayEmpty fullWidth value={filters.status} onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Open">Open</MenuItem>
                        <MenuItem value="In Development">In Development</MenuItem>
                        <MenuItem value="Resolved">Resolved</MenuItem>
                        <MenuItem value="Fix Deployed">Fix Deployed</MenuItem>
                        <MenuItem value="Testing in progress">Testing in progress</MenuItem>
                        <MenuItem value="Fix not working">Fix not working</MenuItem>
                        <MenuItem value="Closed">Closed</MenuItem>
                        <MenuItem value="Backlog">Backlog</MenuItem>
                        <MenuItem value="Defaulter">Defaulter</MenuItem>
                    </Select>
                </TableCell>
            );
        }
        if (colId === 'priority') {
            return (
                <TableCell key={colId}>
                    <Select size="small" displayEmpty fullWidth value={filters.priority} onChange={(e) => setFilters(f => ({ ...f, priority: e.target.value }))}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Critical">Critical</MenuItem>
                        <MenuItem value="High">High</MenuItem>
                        <MenuItem value="Medium">Medium</MenuItem>
                        <MenuItem value="Low">Low</MenuItem>
                    </Select>
                </TableCell>
            );
        }
        return (
            <TableCell key={colId}>
                <TextField size="small" placeholder="Filter..." value={filters[def.filterKey] || ''} onChange={(e) => setFilters(f => ({ ...f, [def.filterKey]: e.target.value }))} fullWidth />
            </TableCell>
        );
    }, [filters]);

    // Memoized lookup map for JIRA users to avoid repeated .find() calls
    const jiraUsersMap = useMemo(() => {
        const map = {};
        jiraUsers.forEach(user => {
            map[user.accountId] = user;
        });
        return map;
    }, [jiraUsers]);

    const fetchIssues = async () => {
        // Skip fetching if issues are provided externally
        if (externalIssues !== undefined) return;
        
        setInternalLoading(true);
        try {
            const data = await issueService.getAll();
            setInternalIssues(data);
        } catch (error) {
            console.error('Error fetching issues:', error);
        } finally {
            setInternalLoading(false);
        }
    };

    const fetchJiraUsers = async () => {
        try {
            const response = await issueService.getJiraUsers();
            setJiraUsers(response);
        } catch (error) {
            console.error('Error fetching JIRA users:', error);
        }
    };

    const fetchModules = async () => {
        try {
            const response = await api.get('/modules');
            setModules(response.data);
        } catch (error) {
            console.error('Error fetching modules:', error);
        }
    };

    const fetchSubModules = async () => {
        try {
            const response = await api.get('/sub-modules/');
            setSubModules(response.data);
        } catch (error) {
            console.error('Error fetching sub-modules:', error);
        }
    };

    const fetchFeatures = async () => {
        try {
            const response = await api.get('/features/');
            setFeatures(response.data);
        } catch (error) {
            console.error('Error fetching features:', error);
        }
    };

    const checkJiraConnection = async () => {
        try {
            const response = await api.get('/jira/status');
            setJiraConnected(response.data.connected === true);
        } catch (error) {
            console.error('Error checking JIRA connection:', error);
            setJiraConnected(false);
        }
    };

    const fetchReleases = async () => {
        try {
            const data = await releasesAPI.getAll();
            setReleases(data);
        } catch (error) {
            console.error('Error fetching releases:', error);
        }
    };

    useEffect(() => {
        fetchIssues();
        fetchJiraUsers();
        fetchModules();
        fetchSubModules();
        fetchFeatures();
        fetchReleases();
        checkJiraConnection();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refreshTrigger, externalIssues]);

    // Fetch scopes for all releases
    useEffect(() => {
        const fetchAllScopes = async () => {
            if (releases.length === 0) return;
            try {
                const results = await Promise.all(
                    releases.map(r => getReleaseScopes(r.id).then(res => res.data).catch(() => []))
                );
                setScopes(results.flat());
            } catch (error) {
                console.error('Error fetching scopes:', error);
            }
        };
        fetchAllScopes();
    }, [releases]);

    // Auto-open side panel for deep-linked issue
    useEffect(() => {
        if (autoExpandIssueId && issues.length > 0) {
            const target = issues.find(i => i.id === autoExpandIssueId);
            if (target && !panelOpen) {
                setSelectedIssue(target);
                setPanelOpen(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoExpandIssueId, issues]);

    const handleUpdate = async (issueId, updates) => {
        try {
            await issueService.update(issueId, updates);
            // Update local state
            const updated = issues.map(issue => 
                issue.id === issueId ? { ...issue, ...updates } : issue
            );
            setIssues(updated);
            // Keep panel in sync
            if (selectedIssue?.id === issueId) {
                setSelectedIssue(prev => ({ ...prev, ...updates }));
            }
        } catch (error) {
            console.error('Error updating issue:', error);
            throw error;
        }
    };

    const handleDelete = async (issueId) => {
        if (window.confirm('Are you sure you want to delete this issue?')) {
            try {
                await issueService.delete(issueId);
                setIssues(issues.filter(issue => issue.id !== issueId));
                // Notify parent to refresh stats
                if (onDeleteCallback) {
                    onDeleteCallback(issueId);
                }
            } catch (error) {
                console.error('Error deleting issue:', error);
                alert('Failed to delete issue');
            }
        }
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const applyFilters = (issue) => {
        // Filter by release ID if provided (from URL param)
        if (defaultReleaseId && issue.release_id !== defaultReleaseId) return false;
        
        if (filters.id && !issue.id.toString().includes(filters.id)) return false;
        if (filters.title && !issue.title.toLowerCase().includes(filters.title.toLowerCase())) return false;
        if (filters.storyId && (!issue.jira_story_id || !issue.jira_story_id.toLowerCase().includes(filters.storyId.toLowerCase()))) return false;
        if (filters.bugId && (!issue.jira_bug_id || !issue.jira_bug_id.toLowerCase().includes(filters.bugId.toLowerCase()))) return false;
        if (filters.module && (!issue.module || !issue.module.name.toLowerCase().includes(filters.module.toLowerCase()))) return false;
        if (filters.subModule && (!issue.sub_module || !issue.sub_module.name.toLowerCase().includes(filters.subModule.toLowerCase()))) return false;
        if (filters.feature && (!issue.feature || !issue.feature.name.toLowerCase().includes(filters.feature.toLowerCase()))) return false;
        if (filters.scope && (!issue.scope || !issue.scope.name.toLowerCase().includes(filters.scope.toLowerCase()))) return false;
        if (filters.status && issue.status !== filters.status) return false;
        if (filters.priority && issue.priority !== filters.priority) return false;
        
        // For assignee filter, check both jira_assignee_name and lookup from jiraUsersMap
        if (filters.assignee) {
            const assigneeName = issue.jira_assignee_name || 
                (issue.jira_assignee_id && jiraUsersMap[issue.jira_assignee_id]?.displayName);
            if (!assigneeName || !assigneeName.toLowerCase().includes(filters.assignee.toLowerCase())) {
                return false;
            }
        }
        
        if (filters.reporter && (!issue.reporter_name || !issue.reporter_name.toLowerCase().includes(filters.reporter.toLowerCase()))) return false;
        return true;
    };

    const filteredIssues = issues.filter(applyFilters);

    // Compute stats from release-filtered issues (before column text filters)
    const releaseFilteredIssues = useMemo(() => {
        if (!defaultReleaseId) return issues;
        return issues.filter(issue => issue.release_id === defaultReleaseId);
    }, [issues, defaultReleaseId]);

    const issueTrackerStats = useMemo(() => {
        if (!releaseFilteredIssues || releaseFilteredIssues.length === 0) return null;
        const statusCounts = { open: 0, in_progress: 0, resolved: 0, fix_deployed: 0, testing_in_progress: 0, fix_not_working: 0, closed: 0, backlog: 0, defaulter: 0 };
        const priorityCounts = {};
        const moduleCounts = {};
        releaseFilteredIssues.forEach((issue) => {
            const s = (issue.status || '').toLowerCase();
            if (s === 'open') statusCounts.open++;
            else if (s === 'in development') statusCounts.in_progress++;
            else if (s === 'resolved') statusCounts.resolved++;
            else if (s === 'fix deployed') statusCounts.fix_deployed++;
            else if (s === 'testing in progress') statusCounts.testing_in_progress++;
            else if (s === 'fix not working') statusCounts.fix_not_working++;
            else if (s === 'closed') statusCounts.closed++;
            else if (s === 'backlog') statusCounts.backlog++;
            else if (s === 'defaulter') statusCounts.defaulter++;
            if (issue.priority) {
                const p = issue.priority.toLowerCase();
                priorityCounts[p] = (priorityCounts[p] || 0) + 1;
            }
            if (issue.module?.name) {
                moduleCounts[issue.module.name] = (moduleCounts[issue.module.name] || 0) + 1;
            }
        });
        return {
            total_issues: releaseFilteredIssues.length,
            ...statusCounts,
            by_priority: priorityCounts,
            by_module: Object.entries(moduleCounts).map(([module_name, count]) => ({ module_name, count })),
        };
    }, [releaseFilteredIssues]);

    return (
        <Box>
            {/* Issue Tracker Summary Tiles */}
            <IssueTrackerSummary
                stats={issueTrackerStats}
                onTileClick={handleTileClick}
                activeFilter={filters.status}
                onModuleClick={handleModuleChipClick}
                activeModuleFilter={filters.module}
            />

            {/* Reset column order button – only visible when order differs from default */}
            {JSON.stringify(columnOrder) !== JSON.stringify(DEFAULT_COLUMN_ORDER) && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
                    <Tooltip title="Reset column order to default">
                        <Button size="small" onClick={handleResetColumnOrder} startIcon={<DragIcon />}>
                            Reset Columns
                        </Button>
                    </Tooltip>
                </Box>
            )}

            <TableContainer component={Paper} sx={{ overflowX: 'auto', maxWidth: '100%' }}>
                <Table sx={{ width: '100%', tableLayout: 'fixed' }} size="small">
                    <TableHead>
                        {/* ----- Header row (draggable) ----- */}
                        <TableRow>
                            {columnOrder.map(colId => {
                                const def = COLUMN_DEFS[colId];
                                return (
                                    <ResizableTableCell
                                        key={colId}
                                        minWidth={def.minWidth}
                                        initialWidth={def.initialWidth}
                                        isHeader={true}
                                        align={def.align}
                                        draggable
                                        onDragStart={() => handleDragStart(colId)}
                                        onDragOver={handleDragOver}
                                        onDrop={() => handleDrop(colId)}
                                        sx={{ cursor: 'grab', userSelect: 'none', '&:active': { cursor: 'grabbing' } }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: def.align === 'left' ? 'flex-start' : 'center', gap: 0.5 }}>
                                            <DragIcon sx={{ fontSize: 16, color: 'text.secondary', opacity: 0.5 }} />
                                            {def.label}
                                        </Box>
                                    </ResizableTableCell>
                                );
                            })}
                        </TableRow>
                        {/* ----- Filter row ----- */}
                        <TableRow>
                            {columnOrder.map(colId => renderFilterCell(colId))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredIssues
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((issue) => (
                                <IssueRow 
                                    key={issue.id} 
                                    issue={issue} 
                                    onEdit={onEdit}
                                    onDelete={handleDelete}
                                    onUpdate={handleUpdate}
                                    jiraUsers={jiraUsers}
                                    jiraUsersMap={jiraUsersMap}
                                    modules={modules}
                                    subModules={subModules}
                                    features={features}
                                    scopes={scopes}
                                    jiraConnected={jiraConnected}
                                    onRefresh={fetchIssues}
                                    onRowClick={handleRowClick}
                                    isSelected={selectedIssue?.id === issue.id}
                                    columnOrder={columnOrder}
                                />
                            ))}
                        {issues.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={columnOrder.length} align="center">
                                    No issues found
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredIssues.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <IssueDetailPanel
                open={panelOpen}
                onClose={handlePanelClose}
                issue={selectedIssue}
                onIssueUpdated={handlePanelIssueUpdated}
                modules={modules}
                subModules={subModules}
                features={features}
                releases={releases}
                jiraUsers={jiraUsers}
                jiraUsersMap={jiraUsersMap}
                jiraConnected={jiraConnected}
            />
        </Box>
    );
};

export default IssueList;
